import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";
import {
  clearAuthState,
  getSavedAuthSession,
  getSavedAuthState,
  recordUserActivity,
  saveAuthState,
} from "../utils/authStorage";
import {
  AUTH_ACTIVITY_WRITE_INTERVAL_MS,
  AUTH_ERROR_MESSAGES,
} from "../constants/auth";
import { fetchCurrentUser } from "../services/authApi";
import {
  completeNewPasswordChallenge,
  signInWithCognito,
  signOutCognitoUser,
} from "../services/cognitoAuth";
import {
  AUTH_SESSION_EXPIRED_EVENT,
  AUTH_SESSION_UPDATED_EVENT,
  refreshAuthSession,
} from "../services/authSession";

const AuthContext = createContext(null);

function buildSession(tokenSession, meResponse) {
  return {
    ...tokenSession,
    user: meResponse.user,
    permissions: meResponse.permissions || [],
    modules: meResponse.modules || [],
    moduleAccess: meResponse.module_access || {},
  };
}

export function AuthProvider({ children }) {
  const navigate = useNavigate();
  const [session, setSession] = useState(getSavedAuthSession);
  const [passwordChallenge, setPasswordChallenge] = useState(null);
  const lastActivityWriteRef = useRef(0);
  const isAuthenticated = getSavedAuthState() && Boolean(session);

  useEffect(() => {
    const handleActivity = () => {
      const now = Date.now();

      if (
        !getSavedAuthSession() ||
        now - lastActivityWriteRef.current < AUTH_ACTIVITY_WRITE_INTERVAL_MS
      ) {
        return;
      }

      lastActivityWriteRef.current = now;
      recordUserActivity(now);
    };
    const handleSessionUpdated = (event) => setSession(event.detail);
    const handleSessionExpired = () => {
      setSession(null);
      setPasswordChallenge(null);
      navigate("/", {
        replace: true,
        state: { message: AUTH_ERROR_MESSAGES.sessionExpired },
      });
    };
    const activityEvents = ["pointerdown", "keydown", "scroll", "touchstart"];

    activityEvents.forEach((eventName) =>
      window.addEventListener(eventName, handleActivity, { passive: true }),
    );
    window.addEventListener(AUTH_SESSION_UPDATED_EVENT, handleSessionUpdated);
    window.addEventListener(AUTH_SESSION_EXPIRED_EVENT, handleSessionExpired);

    const sessionCheckTimer = window.setInterval(() => {
      refreshAuthSession().catch(() => {
        // Refresh failure publishes an expiry event and redirects to login.
      });
    }, 30 * 1000);

    refreshAuthSession().catch(() => {
      // Initial refresh failure follows the same expiry event path.
    });

    return () => {
      activityEvents.forEach((eventName) =>
        window.removeEventListener(eventName, handleActivity),
      );
      window.removeEventListener(
        AUTH_SESSION_UPDATED_EVENT,
        handleSessionUpdated,
      );
      window.removeEventListener(
        AUTH_SESSION_EXPIRED_EVENT,
        handleSessionExpired,
      );
      window.clearInterval(sessionCheckTimer);
    };
  }, [navigate]);

  const persistSession = useCallback(async (tokenSession) => {
    const meResponse = await fetchCurrentUser(tokenSession.accessToken);
    const nextSession = buildSession(tokenSession, meResponse);

    saveAuthState(nextSession);
    setSession(nextSession);
    setPasswordChallenge(null);

    return nextSession;
  }, []);

  const value = useMemo(
    () => ({
      session,
      isAuthenticated,
      user: session?.user || null,
      role: session?.user?.role || null,
      modules: session?.modules || [],
      moduleAccess: session?.moduleAccess || {},
      passwordChallenge,
      hasModuleAccess: (moduleKey) =>
        Boolean(session?.moduleAccess?.[moduleKey]),
      login: async ({ username, password }) => {
        const result = await signInWithCognito(username, password);

        if (result.type === "NEW_PASSWORD_REQUIRED") {
          setPasswordChallenge(result.challenge);
          return { challenge: "NEW_PASSWORD_REQUIRED" };
        }

        await persistSession(result.tokenSession);
        return { success: true };
      },
      completePasswordReset: async ({ newPassword, attributes }) => {
        const result = await completeNewPasswordChallenge({
          challenge: passwordChallenge,
          newPassword,
          attributes,
        });

        await persistSession(result.tokenSession);
        return { success: true };
      },
      logout: () => {
        signOutCognitoUser();
        clearAuthState();
        setSession(null);
        setPasswordChallenge(null);
      },
    }),
    [isAuthenticated, passwordChallenge, persistSession, session],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error("useAuth must be used inside AuthProvider");
  }

  return context;
}
